import { Button, Col, Form, notification, Row, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { useLazyGetDistrictListQuery, useLazyGetWardListQuery } from "../../../redux/api/SystemApi";
import { FloatInput, FloatTextArea } from '../../common/components/Index'
import { CityResponse, DistrictResponse, WardResponse } from "../../common/types/types";
import { useCheckRegisterEmailMutation, useCheckRegisterIdentificationMutation, useCheckRegisterPhoneMutation, useRegisterAccountMutation } from "../redux/RegisterApi";
import { RegisterAccount } from "../types";
import ReCAPTCHA from "react-google-recaptcha";
import { useAppDispatch, useAppSelector } from "../../../redux/Hooks";
import { toggleRegisterModal } from "../redux/RegisterSlice";
import { useNavigate } from "react-router-dom";
import { patterns } from "../../common/ts/CommonFunctions";
import { helpers } from "../../../assets/js/helper";
import { useLazyVerifyGoogleCaptchaQuery } from "../../../redux/api/PublicApi";

interface RegisterProps {
    onRegisterSuccess: () => void;
    openLogin: () => void;
    province: CityResponse[];
    checkDirty: boolean;
    setCancelVisible: React.Dispatch<React.SetStateAction<boolean>>,
    setCheckDirty: React.Dispatch<React.SetStateAction<boolean>>,
}

const defaultFormValues = {
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    province: "",
    ward: "",
    address: "",
}

interface ErrorList {
    [key: string]: string
}

const RegisterForm = ({ onRegisterSuccess, province, openLogin, checkDirty, setCancelVisible, setCheckDirty }: RegisterProps) => {
    const [form] = Form.useForm();
    const [count, setCount] = useState<any>([]);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [wardList, setWardList] = useState<WardResponse[]>([]);
    const [districtList, setDistrictList] = useState<DistrictResponse[]>([]);
    const [errorList, setErrorList] = useState<ErrorList>();

    const [registerAccount, { isLoading }] = useRegisterAccountMutation();
    const [checkRegisterPhone] = useCheckRegisterPhoneMutation();
    const [checkRegisterEmail] = useCheckRegisterEmailMutation();
    const [checkRegisterIdentification] = useCheckRegisterIdentificationMutation();
    const [districtTrigger] = useLazyGetDistrictListQuery();
    const [wardTrigger] = useLazyGetWardListQuery();
    const [verifyCaptcha] = useLazyVerifyGoogleCaptchaQuery();

    const userState = useAppSelector(state => state.login);
    const dispatch = useAppDispatch();

    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (form && checkDirty) {
            let values = form.getFieldsValue();
            let isDirty = false;
            Object.keys(values).forEach(item => {
                if ((values[item] || (typeof (values[item]) === "number"))
                    && values[item] !== defaultFormValues[item as keyof typeof defaultFormValues]) {
                    isDirty = true;
                }
            })

            setCheckDirty(false);

            if (!isDirty) {
                dispatch(toggleRegisterModal());
            } else {
                setCancelVisible(true);
            }
        }
    }, [checkDirty, form, setCheckDirty, setCancelVisible, dispatch])

    useEffect(() => {
        if (form && userState.tempEmail) {
            form.setFieldsValue({
                email: userState.tempEmail
            })
        }
    }, [form, userState.tempEmail])

    const onValuesChange = (changedValues: any) => {
        let key = Object.keys(changedValues)[0];
        let value = changedValues[key];
        if (typeof (value === "string")) {
            setCount({
                ...count,
                [key]: value.length,
            });

            if (errorList && Object.keys(errorList).includes(key)) {
                let newErrorList = { ...errorList };
                delete newErrorList[key];
                setErrorList(newErrorList);
            }
        }

        if (key === "province") {
            if(changedValues.province) {
                districtTrigger({ page: 0, size: 1000, provinceId: changedValues.province }).unwrap().then(res => {
                    setDistrictList([...res.content].sort((a, b) => a.districtName.localeCompare(b.districtName)));
                })
            } else {
                setDistrictList([])
            }
            setWardList([]);
            form.setFieldsValue({
                district: undefined,
                ward: undefined
            })
        }

        if (key === "district") {
            if(changedValues.district) {
                wardTrigger({ page: 0, size: 1000, districtId: changedValues.district }).unwrap().then(res => {
                    setWardList([...res.content].sort((a, b) => a.communeName.localeCompare(b.communeName)));
                })
            } else {
                setWardList([])
            }
            form.setFieldsValue({
                ward: undefined
            })
        }
    }

    const onFinish = (values: any) => {
        let submitData: RegisterAccount = {
            fullName: values.name,
            email: values.email,
            password: values.password,
            phoneNumber: values.phone,
            identificationNumber: values.identificationNumber,
            roleCode: "USER",
            status: 1,
            provinceId: values.province,
            districtId: values.district,
            communeId: values.ward,
            address: values.address
        }

        let isPhoneValid = false;
        let isEmailValid = false;
        let isIdentificationValid = false;
        let newErrorList: ErrorList = {};

        Promise.all([
            checkRegisterPhone(submitData).unwrap(),
            checkRegisterEmail(submitData).unwrap(),
            checkRegisterIdentification(submitData).unwrap()]).then(res => {
                if (res[0] === true) {
                    newErrorList["phone"] = "Số điện thoại đã tồn tại";
                } else if (res[0] === false) {
                    isPhoneValid = true;
                }

                if (res[1] === true) {
                    newErrorList["email"] = "Email đã tồn tại";
                } else if (res[0] === false) {
                    isEmailValid = true;
                }

                if (res[2] === true) {
                    newErrorList["identificationNumber"] = "Số CMT/CCCD/Hộ chiếu đã tồn tại";
                } else if (res[0] === false) {
                    isIdentificationValid = true;
                }

                if (isPhoneValid && isEmailValid && isIdentificationValid) {
                    registerAccount(submitData).unwrap().then(res => {
                        // notification.success({
                        //     message: "Đăng ký tài khoản thành công",
                        // })
                        onRegisterSuccess();
                        // navigate(-1)
                    }).catch(err => {
                        notification.error({
                            message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                        })

                        if (divRef.current) {
                            divRef.current.scrollIntoView({ behavior: 'smooth' });
                        }
                    })
                } else {
                    setErrorList(newErrorList);
                    if (divRef.current) {
                        divRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            })
    }

    const onValidate = (token: string | null) => {
        if (token) {
            verifyCaptcha(token).unwrap().then(res => {
                if (res.success) {
                    setButtonDisabled(false);
                } else {
                    notification.error({ message: "Lỗi Captcha. Vui lòng thử lại sau!" });
                }
            }).catch(err => {
                console.log(err);
                notification.error({ message: "Lỗi Captcha. Vui lòng thử lại sau!" });
            })
        } else {
            setButtonDisabled(true);
        }
    }

    const checkEmptyInput = (_: any, value: string, massage: string) => {
        if (!value?.trim()) {
            return Promise.reject(new Error(massage))
        }

        return Promise.resolve();
    }

    return (
        <Form
            className="custom-form validation-form"
            style={{ marginTop: 15, marginBottom: 45 }}
            name="register"
            form={form}
            colon={false}
            onValuesChange={onValuesChange}
            onFinish={onFinish}
        >
            <Row gutter={[30, 0]} ref={divRef}>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="name"
                        rules={[{ required: true, message: "Vui lòng nhập họ và tên.", whitespace: true }]}>
                        <FloatInput label="Họ và tên" placeholder="Họ và tên" maxLength={50} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.name || 0}/50</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="email"
                        rules={[
                            { validator: (_, value) => checkEmptyInput(_, value, "Vui lòng nhập email.") },
                            { required: true, type: 'email', message: "Vui lòng nhập email đúng định dạng." },
                        ]}
                        status={errorList?.email ? "error" : ""}
                        extra={errorList?.email ? <span className="text-danger font-size-12">{errorList.email}</span> : ""}>
                        <FloatInput label="Email" placeholder="Email" maxLength={50} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.email || 0}/50</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="phone"
                        rules={[
                            { required: true, message: "Vui lòng nhập số điện thoại." },
                            { pattern: patterns.PHONE, message: "Số điện thoại không đúng." }
                        ]}
                        status={errorList?.phone ? "error" : ""}
                        extra={errorList?.phone ? <span className="text-danger font-size-12">{errorList.phone}</span> : ""}>
                        <FloatInput label="Số điện thoại" placeholder="Số điện thoại" maxLength={12} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.phone || 0}/12</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="identificationNumber"
                        rules={[
                            { validator: (_, value) => checkEmptyInput(_, value, "Vui lòng nhập CMT/CCCD/Hộ chiếu.") },
                            { required: true, message: "Vui lòng nhập số CMT/CCCD/Hộ chiếu." },
                        ]}
                        status={errorList?.identificationNumber ? "error" : ""}
                        extra={errorList?.identificationNumber ? <span className="text-danger font-size-12">{errorList.identificationNumber}</span> : ""}>
                        <FloatInput label="CMT/CCCD/Hộ chiếu" placeholder="CMT/CCCD/Hộ chiếu" maxLength={20} required />
                    </Form.Item>
                    <label className="input-show-count">{count.identificationNumber || 0}/20</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="password"
                        rules={[{
                            required: true,
                            message: "Vui lòng nhập mật khẩu."
                        },
                        {
                            pattern: patterns.PASSWORD,
                            message: "Tối thiểu 8 kí tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt."
                        },
                        ]}>
                        <FloatInput label="Mật khẩu" placeholder="Mật khẩu" maxLength={20} isPassword={true} required autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.password || 0}/20</label>
                </Col>
                <Col span={12}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="confirmPassword"
                        dependencies={["password"]}
                        rules={[
                            {
                                required: true,
                                message: "Vui lòng nhập lại mật khẩu."
                            },
                            {
                                pattern: patterns.PASSWORD,
                                message: "Tối thiểu 8 kí tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt."
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Mật khẩu nhập lại không khớp!'));
                                },
                            }),
                        ]}>
                        <FloatInput label="Nhập lại mật khẩu" placeholder="Nhập lại mật khẩu" maxLength={20} isPassword={true} required />
                    </Form.Item>
                    <label className="input-show-count">{count.confirmPassword || 0}/20</label>
                </Col>


                <Col span={24}>
                    <Form.Item
                        style={{ marginBottom: 20 }}
                        label={<span className="font-size-18">Địa chỉ <sup style={{ color: 'red' }}>*</sup></span>} />
                </Col>
                <Col span={8}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="province"
                        rules={[
                            { required: true, message: "Vui lòng chọn Tỉnh/Thành phố." },
                        ]}
                    >
                        <Select
                            filterSort={(optionA, optionB) =>
                                optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                            optionFilterProp="label"
                            showSearch
                            placeholder="Tỉnh/Thành phố"
                            className="custom-select"
                            options={province?.sort((a, b) => helpers.compareListByName(a, b, "provinceName")).map(item => ({
                                label: item.provinceName,
                                value: item.provinceId
                            }))}
                        ></Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="district"
                        rules={[
                            { required: true, message: "Vui lòng chọn Quận/Huyện." },
                        ]}
                    >
                        <Select
                            filterSort={(optionA, optionB) =>
                                optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                            optionFilterProp="label"
                            showSearch
                            placeholder="Quận/Huyện"
                            className="custom-select"
                            options={districtList?.sort((a, b) => helpers.compareListByName(a, b, "districtName")).map(item => ({
                                label: item.districtName,
                                value: item.districtId
                            }))}
                        ></Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="ward"
                        rules={[
                            { required: true, message: "Vui lòng chọn Phường/Xã." },
                        ]}
                    >
                        <Select
                            filterSort={(optionA, optionB) =>
                                optionA.label.toLowerCase().localeCompare(optionB.label.toLowerCase())
                            }
                            optionFilterProp="label"
                            showSearch
                            placeholder="Phường/Xã"
                            className="custom-select"
                            options={wardList?.sort((a, b) => helpers.compareListByName(a, b, "communeName")).map(item => ({
                                label: item.communeName,
                                value: item.communeId
                            }))}
                        >
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        validateTrigger={["onChange", "onBlur"]}
                        name="address"
                        rules={[
                            { required: true, message: "Vui lòng nhập địa chỉ chi tiết.", whitespace: true },
                        ]}
                    >
                        <FloatTextArea label="Địa chỉ" placeholder="Nhập địa chỉ số nhà,..." maxLength={200} autoComplete="off" />
                    </Form.Item>
                    <label className="input-show-count">{count.address || 0}/200</label>
                </Col>
            </Row>

            <Row style={{ marginTop: 45 }} align="middle" justify="center">
                <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_KEY || ""}
                    onChange={onValidate}
                    hl="vi"
                />
            </Row>
            <Row style={{ marginTop: 25 }} align="middle" justify="center">
                <Button
                    loading={isLoading}
                    htmlType="submit"
                    className={buttonDisabled ? "button-light-gray" : "button-light-yellow"}
                    style={{ height: 50, width: 240, fontSize: 16 }}
                    disabled={buttonDisabled}>ĐĂNG KÝ</Button>
            </Row>

            <Row style={{ marginTop: 35 }} align="middle" justify="center">
                <span>Bạn đã có tài khoản?<span className="anchor-text" onClick={openLogin}> Đăng nhập ngay</span></span>
            </Row>
        </Form >
    )
}

export default RegisterForm;